/* =========================================================
   El progreso de una ficha concreta, resuelto contra los datos del portal.

   `progreso.ts` es aritmética pura: le das las fichas y te da el número.
   Esto es lo que sabe DÓNDE están esas fichas — qué instructores cuelgan
   de un site, qué sites de una organización, qué documentos tiene.

   Va aparte para que el cálculo se pueda probar sin montar una fuente de
   datos, y para que las ocho pantallas que enseñan un porcentaje no tengan
   que rearmar cada una la misma búsqueda.
   ========================================================= */
import type { Organization, Site } from '@/types';
import { ds } from '@/data';
import { progresoDeOrg, progresoDeSite, type Progreso } from './progreso';

/** Los instructores afiliados a un site. */
function instructoresDe(siteId: string) {
  const ids = new Set(ds.listAssignments({ siteId }).map((a) => a.InstructorId));
  return ds.listInstructors().filter((i) => ids.has(i.InstructorId));
}

/** El progreso de un site: su ficha y la de sus instructores.
 *
 *  Sin compliance: los documentos son de la organización.
 *
 *  `borrador` es la ficha a medio editar de la pantalla de detalle. Se usa
 *  en lugar de la guardada para que el porcentaje se mueva mientras se
 *  escribe, que es la mitad de para qué sirve enseñarlo. */
export function progresoSite(
  siteId: string, borrador?: Record<string, unknown> | null,
): Progreso {
  const guardado = ds.listSites().find((s) => s.SiteId === siteId);
  if (!guardado) return { hechos: 0, total: 0, pct: 100 };
  // el borrador se superpone al guardado: trae solo lo tocado
  const site = borrador ? { ...guardado, ...borrador } as Site : guardado;
  return progresoDeSite(site, instructoresDe(siteId), ds.marcasNA());
}

/** El progreso de una organización: lo suyo, sus sites, sus instructores y
 *  sus documentos, en un solo número.
 *
 *  Un instructor que trabaje en dos sites del mismo socio cuenta una vez.
 *  Contarlo dos inflaría el denominador y haría que el mismo trabajo
 *  valiese el doble. */
export function progresoOrg(
  orgId: string, borrador?: Record<string, unknown> | null,
): Progreso {
  const guardado = ds.listOrgs().find((o) => o.OrgId === orgId);
  if (!guardado) return { hechos: 0, total: 0, pct: 100 };
  const org = borrador ? { ...guardado, ...borrador } as Organization : guardado;
  const sites = ds.listSites({ orgId });
  const idsDeSusSites = new Set(sites.map((s) => s.SiteId));
  const suyos = new Set(
    ds.listAssignments()
      .filter((a) => idsDeSusSites.has(a.SiteId))
      .map((a) => a.InstructorId),
  );
  return progresoDeOrg(
    org,
    sites,
    ds.listInstructors().filter((i) => suyos.has(i.InstructorId)),
    ds.getCompliance(orgId),
    ds.marcasNA(),
  );
}
